import React, {useState} from "react"
import styled from "styled-components"
import {DatePicker, Input, Select} from "@irati/ui/src/FormItem"
import Spacing from "@irati/ui/src/Spacing"
import {Body, H3, H4} from "@irati/ui/src/Typography"
import Button from "@irati/ui/src/Button"
import {ButtonRouterLink} from "@irati/ui/src/Link"
import Item from "../components/Item"
import {useBasketDataQuery, useCreateStoreOrderMutation} from "@irati/payrollpay/hooks"
import {useStoreContext} from "../context/StoreContext"
import Spinner from "@irati/ui/src/Loading/Spinner"
import ThankYouScreen from "./ThankYouScreen"

const detailsFields = {
  placeOfWork: {label: "Place of work", name: "placeOfWork", value: "The Office"},
  employeeNumber: {label: "Employee number", name: "employeeNumber", value: "BACKEND_EMPLOYEE_NUMBER"},
  niNumber: {label: "National Insurance Number", name: "niNumber", value: "AB123456A"},
  firstName: {label: "First Name", name: "firstName", value: "Vivup"},
  lastName: {label: "Last Name", name: "lastName", value: "Team"},
  email: {label: "Email address", name: "email", value: "hello@vivup.co.uk"},
  mobileNumber: {label: "Mobile number", name: "mobileNumber", value: "07987654321"},
  phoneNumber: {label: "Phone number", name: "phoneNumber", value: "07987654321"}
}

const addressFields = {
  lineOne: {label: "Line 1", name: "lineOne", value: "Surrey Technology Centre"},
  lineTwo: {label: "Line 2", name: "lineTwo", value: "Occam Road, Surrey Research Park"},
  town: {label: "Town", name: "town", value: "Guildford"},
  county: {label: "County", name: "county", value: "Surrey"},
  postCode: {label: "Post Code", name: "postCode", value: "GU2 7YG"}
}

const CheckoutScreen = () => {
  const [activeStep, setActiveStep] = useState<"details" | "delivery">("details")
  const {schemeType} = useStoreContext()
  const [createStoreOrder, {loading: submitLoading}] = useCreateStoreOrderMutation()
  const [orderId, setOrderId] = useState<string>()

  const {data: basketData, loading} = useBasketDataQuery(
    {
      organisationId: "566-zz-nhs-demo",
      schemeType,
      locale: "en-GB"
    },
    {
      fetchPolicy: "cache-and-network"
    }
  )

  const basket = basketData?.employeeOrganisation?.scheme?.basket
  const isBasketEmpty = !basket?.basketItems?.length || basket?.count === 0

  if (loading) {
    return <Spinner />
  }

  if (orderId) {
    return <ThankYouScreen orderId={orderId} />
  }

  if (isBasketEmpty) {
    return (
      <OuterContainer>
        <TitleContainer>
          <Title>Checkout</Title>
        </TitleContainer>
        <Spacing paddingTop={2}>
          <Body>Your basket is empty</Body>
        </Spacing>
        <ButtonRow>
          <StyledRouterLink to="/products">&lt; Continue shopping</StyledRouterLink>
        </ButtonRow>
      </OuterContainer>
    )
  }

  const handleButtonClick = async () => {
    if (activeStep === "details") {
      setActiveStep("delivery")
    } else {
      const orderPayload = {
        input: {
          organisationId: "566-zz-nhs-demo",
          schemeType: schemeType,
          placeOfWork: detailsFields.placeOfWork.value,
          employeeNumber: detailsFields.employeeNumber.value,
          niNumber: detailsFields.niNumber.value,
          title: "mr",
          dateOfBirth: "1990-01-01",
          firstName: detailsFields.firstName.value,
          lastName: detailsFields.lastName.value,
          mobileNumber: detailsFields.mobileNumber.value,
          telephoneNumber: detailsFields.phoneNumber.value,
          email: detailsFields.email.value,
          deliveryAddress1: addressFields.lineOne.value,
          deliveryAddress2: addressFields.lineTwo.value,
          deliveryTown: addressFields.town.value,
          deliveryCounty: addressFields.county.value,
          deliveryPostcode: addressFields.postCode.value,
          termsAcknowledged: true,
          hireAgreementAcknowledged: true,
          salarySacrificeArrangementAcknowledged: true
        }
      }

      try {
        const res = await createStoreOrder({
          variables: {
            ...orderPayload
          }
        })

        const newOrderId = res?.createStoreOrder?.order?.id

        if (newOrderId) {
          setOrderId(newOrderId)
        } else {
          console.error("Order ID not found")
        }
      } catch (error) {
        console.error("Error submitting order:", error)
      }
    }
  }

  return (
    <OuterContainer>
      <FormContainer>
        <Steps>
          <StepItem onClick={() => setActiveStep("details")}>
            <ItemNumber isActive={activeStep === "details"}>1</ItemNumber>
            <ItemLabel>Your details</ItemLabel>
          </StepItem>
          <StepItem onClick={() => setActiveStep("delivery")}>
            <ItemNumber isActive={activeStep === "delivery"}>2</ItemNumber>
            <ItemLabel>Submit order</ItemLabel>
          </StepItem>
        </Steps>

        {activeStep === "details" ? (
          <>
            {Object.values(detailsFields).map((field) => (
              <Input key={field.name} {...field} />
            ))}
            <Spacing margin={[2, 0]}>
              <Select
                label="Title"
                name="title"
                options={[
                  {label: "Mr", value: "mr"},
                  {label: "Ms", value: "ms"}
                ]}
                onChange={() => {}}
                formatMessage={{id: ""}}
              />
            </Spacing>

            <DatePicker label="Date of Birth" icon />
          </>
        ) : (
          <>
            <Spacing padding={[1, 2]}>
              <Body>This is the address your employer has on record for you</Body>
            </Spacing>
            {Object.values(addressFields).map((field) => (
              <Input key={field.name} {...field} />
            ))}
          </>
        )}
        <Spacing marginTop={2} padding={[0, 2]}>
          {orderId ? (
            <ButtonRouterLink full primary marginTop="33px" to={`/thank-you?orderId=${orderId}`}>
              Go to Thank You
            </ButtonRouterLink>
          ) : (
            <Button full primary marginTop="33px" onClick={handleButtonClick} disabled={submitLoading}>
              {activeStep === "details" ? "Continue to delivery" : submitLoading ? "Submitting..." : "Submit order"}
            </Button>
          )}
        </Spacing>
      </FormContainer>

      <Spacing hideOnMobile style={{width: "51%"}}>
        <OrderSummary>
          <SummaryContent>
            <SummaryTitle>Order summary</SummaryTitle>
            {basket?.basketItems?.map(({imageUrl, name, price, id}) => {
              return <Item imageUrl={imageUrl || ""} name={name || ""} price={price || 0} id={id || ""} key={id} />
            })}

            <Spacing padding={[2.5, 0]}>
              <H4>{basket?.term || 0} month term</H4>
              <Body margin={[1, 0]}>Total monthly amount from take home pay ({basket?.count || 0} items):</Body>

              <Body margin={[1.5, 0]}>
                <Price>£{basket?.monthlyPrice?.toFixed(2) || 0}</Price>
                &nbsp; per month
              </Body>

              <Body>Total gross monthly reduction:</Body>
              <Body>£{basket?.totalMonthlyGrossAmount || 0} as seen on payslip</Body>
            </Spacing>
          </SummaryContent>
        </OrderSummary>
      </Spacing>
    </OuterContainer>
  )
}

export default CheckoutScreen

const OuterContainer = styled.div`
  padding: 0 12px;
  max-width: 1380px;
  margin: 0 auto;
  display: flex;
  justify-content: space-between;
`

const FormContainer = styled.div`
  width: 100%;

  ${({theme}) => theme.mediaQueries.minWidthMedia.desktop`
    width: 42.5%;
    padding: 0 14px;
    margin-right: 16px;
  `}
`

const Steps = styled.div`
  display: flex;
  padding: 28px 0 26px 28px;
`

const StepItem = styled.div`
  display: flex;
  margin-right: 24px;
  cursor: pointer;
  border-radius: 4px;
  padding: 2px;
`

interface ItemNumberProps {
  isActive: boolean
}

const ItemNumber = styled.div<ItemNumberProps>`
  width: 20px;
  height: 20px;
  color: ${({theme, isActive}) => (isActive ? theme.colors.greys.white : theme.colors.primary1)};
  background-color: ${({theme, isActive}) => (isActive ? theme.colors.primary1 : theme.colors.greys.white)};
  border-radius: 50%;
  border: 1px solid ${({theme}) => theme.colors.primary1};
  font-size: 12px;
  font-weight: ${({theme, isActive}) => (isActive ? 700 : 500)};
  margin-right: 4px;
  text-align: center;
`

const ItemLabel = styled(H4)`
  font-weight: 500;
  align-self: center;
` as typeof H4

const OrderSummary = styled.div`
  $box-shadow: 0rem 0.1875rem 0.375rem #00000029;
  border: 0.0625rem solid ${({theme}) => theme.colors.greys.light};
  border-radius: 16px;
  position: sticky;
  top: 160px;
`

const SummaryContent = styled.div`
  padding: 36px 44px 0 28px;
  color: ${({theme}) => theme.colors.primary1};
  max-height: 60vh;
  overflow: auto;
  position: relative;
`

const SummaryTitle = styled(H3)`
  color: ${({theme}) => theme.colors.primary1_600};
`

const Price = styled(Body)`
  display: inline;
  font-size: 24px;
  font-weight: 700;
  color: ${({theme}) => theme.colors.primary1};
`

const TitleContainer = styled.div`
  display: flex;
  justify-content: space-between;
`

const Title = styled(H3)`
  line-height: 30px;
  font-size: 24px;
  margin: 0;
  font-weight: 700;

  ${({theme}) => theme.mediaQueries.minWidthMedia.desktop`
    line-height: 40px;
    font-size: 32px;
  `}
`

const ButtonRow = styled.div`
  display: flex;
  flex-direction: column-reverse;
  align-items: center;

  ${({theme}) => theme.mediaQueries.minWidthMedia.desktop`
    flex-direction: row;
    justify-content: space-between;
  `}
`

const StyledRouterLink = styled(ButtonRouterLink)`
  margin-top: 12px;
` as typeof ButtonRouterLink
